import { createContext, useContext, useState, useCallback, type ReactNode } from "react";

export interface BottleSelection {
  bottleId: string;
  bottleName: string;
  quantity: number;
  upcharge?: number; // cents per bottle — extra charge for premium bottles on table packages
}

export interface MixerSelection {
  mixerId: string;
  mixerName: string;
}

export interface CartItem {
  type: "ticket" | "table" | "bottle";
  itemId: string;
  name: string;
  quantity: number;
  unitPrice: number; // cents
  tierType?: string;
  // Table-specific selections
  bottleSelections?: BottleSelection[];
  mixerSelections?: MixerSelection[];
  bottleCount?: number; // how many bottles included
  mixerLimit?: number; // max mixers allowed
  specialOccasion?: string[]; // e.g., ["Birthday", "Anniversary"]
  specialNotes?: string; // free-text notes for the table
}

export interface AppliedPromo {
  id: string;
  code: string;
  discount_type: "percentage" | "flat";
  discount_value: number;
  discount_amount: number; // calculated discount in cents
}

interface CartContextValue {
  items: CartItem[];
  guestEmail: string;
  guestPhone: string;
  guestName: string;
  setGuestEmail: (email: string) => void;
  setGuestPhone: (phone: string) => void;
  setGuestName: (name: string) => void;
  addItem: (item: CartItem) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  subtotal: number;
  itemCount: number;
  appliedPromo: AppliedPromo | null;
  setAppliedPromo: (promo: AppliedPromo | null) => void;
  discountedSubtotal: number;
  pendingPromoCode: string | null;
  clearPendingPromoCode: () => void;
}

const CartContext = createContext<CartContextValue | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [guestName, setGuestName] = useState("");
  const [appliedPromo, setAppliedPromo] = useState<AppliedPromo | null>(null);

  // Capture promo code from URL on mount
  const [pendingPromoCode, setPendingPromoCode] = useState<string | null>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("promo");
  });
  const clearPendingPromoCode = useCallback(() => setPendingPromoCode(null), []);

  const addItem = useCallback((item: CartItem) => {
    setItems((prev) => {
      // For tables, always add as new item (each table reservation is unique due to bottle selections)
      if (item.type === "table") {
        return [...prev, { ...item, itemId: `${item.itemId}-${Date.now()}` }];
      }
      const existing = prev.find((i) => i.itemId === item.itemId);
      if (existing) {
        return prev.map((i) =>
          i.itemId === item.itemId ? { ...i, quantity: i.quantity + item.quantity } : i
        );
      }
      return [...prev, item];
    });
  }, []);

  const removeItem = useCallback((itemId: string) => {
    setItems((prev) => prev.filter((i) => i.itemId !== itemId));
  }, []);

  const updateQuantity = useCallback((itemId: string, quantity: number) => {
    if (quantity <= 0) {
      setItems((prev) => prev.filter((i) => i.itemId !== itemId));
    } else {
      setItems((prev) =>
        prev.map((i) => (i.itemId === itemId ? { ...i, quantity } : i))
      );
    }
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    setGuestEmail("");
    setGuestPhone("");
    setGuestName("");
    setAppliedPromo(null);
  }, []);

  const subtotal = items.reduce((sum, i) => {
    let itemTotal = i.unitPrice * i.quantity;
    // Add bottle upcharges for table packages
    if (i.type === "table" && i.bottleSelections) {
      itemTotal += i.bottleSelections.reduce((s, b) => s + (b.upcharge || 0) * b.quantity, 0);
    }
    return sum + itemTotal;
  }, 0);
  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);
  const discountedSubtotal = appliedPromo
    ? Math.max(0, subtotal - appliedPromo.discount_amount)
    : subtotal;

  return (
    <CartContext.Provider
      value={{
        items,
        guestEmail,
        guestPhone,
        guestName,
        setGuestEmail,
        setGuestPhone,
        setGuestName,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        subtotal,
        itemCount,
        appliedPromo,
        setAppliedPromo,
        discountedSubtotal,
        pendingPromoCode,
        clearPendingPromoCode,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}
